import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Award } from 'lucide-react';
import { trackPageView } from '../analytics';
import SEO from './SEO';

const CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes
const CACHE_VERSION = 3; // Increment this to invalidate old cache

const PopularCoins = () => {

  // Helper to check if cache is valid
  const isCacheValid = (cacheData) => {
    if (!cacheData || !cacheData.timestamp || !cacheData.version) return false;
    if (cacheData.version !== CACHE_VERSION) return false; // Version mismatch
    const now = Date.now();
    return (now - cacheData.timestamp) < CACHE_EXPIRY_MS;
  };

  // Restore cached coins from sessionStorage
  const [coins, setCoins] = useState(() => {
    const cached = sessionStorage.getItem('popularCoins_cache');
    if (cached) {
      try {
        const cacheData = JSON.parse(cached);
        if (isCacheValid(cacheData)) {
          return cacheData.coins || [];
        } else {
          // Cache expired, clear it
          sessionStorage.removeItem('popularCoins_cache');
          return [];
        }
      } catch (e) {
        console.error('Error parsing cached coins:', e);
        sessionStorage.removeItem('popularCoins_cache');
        return [];
      }
    }
    return [];
  });

  const [displayCount, setDisplayCount] = useState(() => {
    const cached = sessionStorage.getItem('popularCoins_cache');
    if (cached) {
      try {
        const cacheData = JSON.parse(cached);
        if (isCacheValid(cacheData)) {
          return cacheData.coins?.length || 100;
        }
      } catch (e) {
        // Ignore error
      }
    }
    return 100;
  });

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const scrollTargetRef = useRef(null);

  // Track page view on mount
  useEffect(() => {
    trackPageView('popular_coins');
  }, []);

  // Save coins data to sessionStorage with timestamp whenever it changes
  useEffect(() => {
    if (coins.length > 0) {
      // Cache only the fields we actually use to save space
      const serializableCoins = coins.map(coin => {
        // Convert Firestore Timestamp to ISO string for serialization
        let lastUpdated = coin.last_updated;
        if (lastUpdated && typeof lastUpdated.toDate === 'function') {
          lastUpdated = lastUpdated.toDate().toISOString();
        } else if (lastUpdated && typeof lastUpdated.toMillis === 'function') {
          lastUpdated = new Date(lastUpdated.toMillis()).toISOString();
        }

        return {
          id: coin.id,
          market_cap_rank: coin.market_cap_rank,
          image: coin.image,
          name: coin.name,
          symbol: coin.symbol,
          market_cap: coin.market_cap,
          current_price: coin.current_price,
          last_updated: lastUpdated,
          price_change_percentage_24h: coin.price_change_percentage_24h
        };
      });

      const cacheData = {
        coins: serializableCoins,
        timestamp: Date.now(),
        version: CACHE_VERSION
      };
      sessionStorage.setItem('popularCoins_cache', JSON.stringify(cacheData));
    }
  }, [coins]);

  useEffect(() => {
    async function fetchPopularCoins() {
      try {
        // If we have cached data and it's valid and sufficient for current display count, skip fetch
        const cached = sessionStorage.getItem('popularCoins_cache');

        if (cached) {
          const cacheData = JSON.parse(cached);

          if (isCacheValid(cacheData) && cacheData.coins?.length >= displayCount) {
            setCoins(cacheData.coins);
            setHasMore(cacheData.coins.length < 1000); // Assume more if less than reasonable max
            setLoading(false);
            setLoadingMore(false);
            return;
          } else if (!isCacheValid(cacheData)) {
            // Cache expired, clear it
            sessionStorage.removeItem('popularCoins_cache');
          }
        }

        // Only show full loading spinner on initial load
        if (loading && displayCount === 100) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const coinsRef = collection(db, 'coins');
        // Filter coins with valid market_cap_rank, order by rank
        // Fetch more than we need to determine if there are more coins available
        const q = query(
          coinsRef,
          where('market_cap_rank', '!=', null),
          orderBy('market_cap_rank', 'asc'),
          limit(displayCount + 1)
        );
        const querySnapshot = await getDocs(q);

        const coinsData = [];
        querySnapshot.forEach((doc) => {
          coinsData.push({ id: doc.id, ...doc.data() });
        });

        // Check if there are more coins
        if (coinsData.length > displayCount) {
          setHasMore(true);
          setCoins(coinsData.slice(0, displayCount));
        } else {
          setHasMore(false);
          setCoins(coinsData);
        }

        setLoading(false);
        setLoadingMore(false);
      } catch (err) {
        console.error('Error fetching popular coins:', err);
        setError('Failed to load popular coins');
        setLoading(false);
        setLoadingMore(false);
      }
    }

    fetchPopularCoins();
    // the code reads the loading state variable, but it's not in the dependency array. However, this is actually intentional - we don't
    // want to re-run the effect when loading changes. The proper fix is to use the functional form of setState or add an ESLint disable comment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayCount]);

  // Scroll to newly loaded content after coins update
  useEffect(() => {
    if (scrollTargetRef.current !== null && !loadingMore && coins.length > 0) {
      const targetIndex = scrollTargetRef.current;
      scrollTargetRef.current = null; // Reset after scrolling

      // Wait for render to complete
      setTimeout(() => {
        const tableRows = document.querySelectorAll('tbody tr');
        if (tableRows.length > targetIndex) {
          const targetRow = tableRows[targetIndex];
          if (targetRow) {
            targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
    }
  }, [coins, loadingMore]);

  const handleLoadMore = () => {
    const previousCount = displayCount;
    // Store the index we want to scroll to (the last item before loading more)
    scrollTargetRef.current = previousCount - 1;
    setDisplayCount(prev => prev + 100);
  };

  const formatLargeNumber = (num) => {
    if (!num) return 'N/A';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };

  // Calculate popularity score based on market cap ratio
  const calculatePopularityScore = (coinMarketCap) => {
    if (!coinMarketCap || coins.length === 0) return 0;

    // Get rank 1's market cap (first coin in sorted list)
    const logRank1MarketCap = Math.log(coins[0]?.market_cap);
    if (!logRank1MarketCap) return 0;

    // Formula: (log(marketCap / logRank1MarketCap) + 1) * 10000
    const ratio = Math.log(coinMarketCap) / logRank1MarketCap;
    const score = ratio * 10000;

    return Math.max(0, Math.round(score)); // Ensure non-negative
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
        <p className="text-gray-600 text-lg">Loading popular coins...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-md">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  const scrollToBottom = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SEO
        title="Popular Coins"
        description="Discover the most popular cryptocurrencies ranked by market cap and community interest. View safety ratings, prices, and market data for top coins."
        canonicalUrl="https://coincommons.io/popular-coins"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          'name': 'Popular Cryptocurrencies',
          'description': 'Top cryptocurrencies ranked by popularity on CoinCommons',
          'url': 'https://coincommons.io/popular-coins',
          'numberOfItems': coins.length,
          'itemListElement': coins.slice(0, 10).map((coin, index) => ({
            '@type': 'ListItem',
            'position': index + 1,
            'name': coin.name,
            'url': `https://coincommons.io/coin/${coin.id}`
          }))
        }}
      />
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Award className="w-8 h-8 text-purple-600" />
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">Popular Coins</h1>
            <p className="text-gray-600 text-sm mt-1">
              Top coins ranked by popularity
            </p>
          </div>
        </div>

        {/* Top Navigation Links */}
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
          <Link
            to="/"
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            ← Back to Homepage
          </Link>
          <button
            onClick={scrollToBottom}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            ↓ Jump to Bottom
          </button>
        </div>

        {/* Coins Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-1 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  #
                </th>
                <th className="text-left py-2 px-1 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Coin
                </th>
                <th className="text-right py-2 px-1 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Pop. Score*
                </th>
                <th className="text-right py-2 px-1 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Market Cap
                </th>
                <th className="text-right py-2 px-1 text-xs font-semibold text-gray-600 uppercase tracking-wider  hidden sm:table-cell">
                  Price
                </th>
              </tr>
            </thead>
            <tbody>
              {coins.map((coin) => {
                return (
                  <tr
                    key={coin.id}
                    className="border-b border-gray-100"
                  >
                    {/* Rank */}
                    <td className="py-3 px-1 whitespace-nowrap">
                      <span className="text-gray-600 font-semibold text-xs">
                        #{coin.market_cap_rank}
                      </span>
                    </td>

                    {/* Coin Name&Symbol */}
                    <td className="py-3 px-1">
                      <Link to={`/coin/${coin.id}`} className="flex items-center gap-2 hover:bg-gray-50 transition">
                        <img
                          src={coin.image}
                          alt={coin.name}
                          className="w-6 h-6 rounded-full flex-shrink-0"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24"%3E%3Crect width="24" height="24" fill="%23ddd"/%3E%3C/svg%3E';
                          }}
                        />
                        <div className="min-w-0 max-w-[100px] sm:max-w-none">
                          <div className="font-semibold text-gray-900 text-xs sm:text-sm break-words leading-tight">
                            {coin.name}
                          </div>
                          <div className="text-xs text-gray-500 uppercase break-all">
                            {coin.symbol}
                          </div>
                        </div>
                      </Link>
                    </td>

                    {/* Popularity Score */}
                    <td className="py-3 px-1 text-right whitespace-nowrap">
                      <span className="font-semibold text-purple-600 text-xs">
                        {calculatePopularityScore(coin.market_cap).toLocaleString()}
                      </span>
                    </td>

                    {/* Marketp Cap */}
                    <td className="py-3 px-1 text-right whitespace-nowrap">
                      <span className="text-gray-700 text-xs sm:text-sm">
                        {formatLargeNumber(coin.market_cap)}
                      </span>
                    </td>

                    {/* Price */}
                    <td className="py-3 px-1 text-right hidden sm:table-cell whitespace-nowrap">
                      <span className="text-gray-700 text-xs sm:text-sm">
                        ${coin.current_price ? coin.current_price.toLocaleString(undefined, { maximumFractionDigits: 2 }) : 'N/A'}
                      </span>
                    </td>                    
                    {/*<td className="py-3 px-1 text-right whitespace-nowrap">
                      {priceChange !== null && priceChange !== undefined ? (
                        <div className={`flex items-center justify-end gap-0.5 font-semibold text-xs ${isPriceUp ? 'text-green-600' : 'text-red-600'}`}>
                          {isPriceUp ? <TrendingUp className="w-3 h-3 flex-shrink-0" /> : <TrendingDown className="w-3 h-3 flex-shrink-0" />}
                          <span>
                            {isPriceUp ? '+' : ''}{priceChange.toFixed(1)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </td>*/}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? 'Loading...' : 'Load More (+100)'}
            </button>
          </div>
        )}

        {/* Bottom Navigation Links */}
        <div className="flex items-center gap-4 mt-6 pt-4 border-t border-gray-200">
          <Link
            to="/"
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            ← Back to Homepage
          </Link>
          <button
            onClick={scrollToTop}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            ↑ Jump to Top
          </button>
        </div>

        {/* Footer Note */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Showing top {coins.length} coins ranked by internal popularity score.
            {!hasMore && ' All ranked coins loaded.'}
            <br />
            *Popularity score is calculated by CoinCommons internally based on multiple factors like user visits, market cap etc.
            <br />
            Use it at your own discretion.
          </p>
        </div>

        {/* Data Attribution */}
        {coins.length > 0 && coins[0]?.last_updated && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Powered by{' '}
              <a
                href="https://www.coingecko.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 hover:underline"
              >
                CoinGecko
              </a>
              . Data last updated at {(() => {
                const lastUpdated = coins[0].last_updated;
                if (typeof lastUpdated === 'string') {
                  return new Date(lastUpdated).toLocaleString();
                } else if (lastUpdated && typeof lastUpdated.toDate === 'function') {
                  return lastUpdated.toDate().toLocaleString();
                } else if (lastUpdated && typeof lastUpdated.toMillis === 'function') {
                  return new Date(lastUpdated.toMillis()).toLocaleString();
                } else {
                  return new Date(lastUpdated).toLocaleString();
                }
              })()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PopularCoins;
